import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { fmtDate, fmtKg, batimentColor } from "@/lib/helpers";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Tag, ScanLine } from "lucide-react";
import ScanPalette, { parsePaletteCode, feedback } from "@/components/ScanPalette";

const FILTERS = [
  { key: "all", label: "Tous", testId: "palette-filter-all" },
  { key: "P", label: "Conformes P", testId: "palette-filter-conforme" },
  { key: "DECLASSEE", label: "Déclassées", testId: "palette-filter-declassee" },
  { key: "stock", label: "En stock", testId: "palette-filter-stock" },
  { key: "expediee", label: "Expédiées", testId: "palette-filter-expediee" },
];

export default function ListePalettes() {
  const [palettes, setPalettes] = useState([]);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [scanning, setScanning] = useState(false);
  const [lastScan, setLastScan] = useState(null);
  const navigate = useNavigate();

  const handleCode = async (raw) => {
    const code = parsePaletteCode(raw);
    if (!code) {
      feedback(false);
      return setLastScan({ ok: false, message: `Code non reconnu : ${String(raw).slice(0, 30)}` });
    }
    try {
      const { data: p } = await api.get(`/palettes/by-code/${code}`);
      feedback(true);
      setLastScan({ ok: true, message: `${code} trouvée — ouverture de l'étiquette` });
      navigate(`/palette/${p.id}/etiquette`);
    } catch (err) {
      feedback(false);
      setLastScan({ ok: false, message: apiError(err.response?.data?.detail) });
    }
  };

  const load = () => {
    const params = {};
    if (filter === "P" || filter === "DECLASSEE") params.type = filter;
    if (filter === "stock") params.statut = "en_stock";
    if (filter === "expediee") params.statut = "expediee";
    if (q) params.q = q;
    api.get("/palettes", { params }).then((r) => setPalettes(r.data));
  };

  useEffect(load, [filter, q]);

  return (
    <div className="space-y-5" data-testid="liste-palettes">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading font-black text-3xl uppercase tracking-tight text-slate-900">
          Registre des palettes
        </h1>
        {!scanning && (
          <Button data-testid="registre-scan-btn" onClick={() => { setLastScan(null); setScanning(true); }}
            className="h-14 px-6 bg-slate-900 hover:bg-slate-800 font-bold text-base">
            <ScanLine className="w-6 h-6 mr-2" /> Scanner une palette
          </Button>
        )}
      </div>

      {scanning && (
        <ScanPalette onCode={handleCode} onClose={() => setScanning(false)} lastResult={lastScan}
          hint="Scannez l'étiquette (caméra ou douchette) : la fiche de la palette s'ouvre aussitôt pour réimpression." />
      )}

      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            data-testid={f.testId}
            onClick={() => setFilter(f.key)}
            className={`px-4 py-2 rounded-lg font-bold text-sm uppercase tracking-wide transition-colors ${
              filter === f.key ? "bg-slate-900 text-white" : "bg-white border-2 border-slate-200 text-slate-600 hover:border-slate-400"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="relative">
        <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          data-testid="palette-search-input"
          placeholder="Rechercher par n° palette (ex: AUT-2026-…)"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="pl-10 h-12"
        />
      </div>

      <div className="rounded-2xl bg-white border-2 border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b-2 border-slate-200">
              <tr className="text-left text-xs uppercase tracking-wide text-slate-500 font-bold">
                <th className="px-4 py-3">N° Palette</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Bâtiment</th>
                <th className="px-4 py-3">Ponte</th>
                <th className="px-4 py-3">Motif / Cat.</th>
                <th className="px-4 py-3 text-right">Poids net</th>
                <th className="px-4 py-3">Statut</th>
              </tr>
            </thead>
            <tbody>
              {palettes.map((p) => (
                <tr
                  key={p.id}
                  data-testid={`palette-row-${p.code_qr}`}
                  onClick={() => navigate(`/palette/${p.id}/etiquette`)}
                  className={`border-b border-slate-100 cursor-pointer hover:bg-slate-50 ${
                    p.type === "DECLASSEE" ? "bg-rose-50/50" : ""
                  }`}
                >
                  <td className="px-4 py-3 font-mono font-bold text-slate-900 flex items-center gap-2">
                    {p.type === "DECLASSEE" && <span className="w-2 h-2 rounded-full bg-rose-500" />}
                    {p.code_qr}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded font-bold text-xs uppercase ${
                      p.type === "P" ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"
                    }`}>
                      {p.type === "P" ? "Conforme" : "Déclassée"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded font-bold text-xs ${batimentColor(p.batiment)}`}>{p.batiment}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{fmtDate(p.date_ponte)}</td>
                  <td className="px-4 py-3 text-slate-600">{p.type === "P" ? `Cat. ${p.categorie}` : p.motif}</td>
                  <td className="px-4 py-3 text-right font-mono font-bold">{fmtKg(p.poids_net)}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase ${
                      p.statut === "en_stock" ? "bg-slate-100 text-slate-700" : "bg-blue-100 text-blue-800"
                    }`}>
                      {p.statut === "en_stock" ? "En stock" : "Expédiée"}
                    </span>
                  </td>
                </tr>
              ))}
              {palettes.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-slate-400 font-medium">
                    <Tag className="w-8 h-8 mx-auto mb-2 opacity-40" />
                    Aucune palette trouvée.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
